import { DEV } from 'esm-env';
import { CustomError } from './core.ts';
import { Tracked } from './disposable.ts';
import { isPresent, recordEntries, recordFromEntries, type RecordKey } from './identity.ts';
import type { Scalar, Time } from './units.ts';
import { Milliseconds } from './units.ts';

export async function allRecordPromises<U extends RecordKey, T>(record: Record<U, Promise<T>>) {
  const entries = recordEntries(record);
  const results = await Promise.all(entries.map(([_, v]) => v));
  return recordFromEntries(results.map((r, i) => [entries[i][0], r]));
}

export async function fromGenerator<T>(generator: AsyncGenerator<T, void, void>) {
  const results: Array<T> = [];
  for await (const result of generator) {
    results.push(result);
  }
  return results;
}

// Useful for on-demand initialization of globals.
export class Lazy<T> {
  private value_: T | undefined;

  get value() {
    this.value_ ??= this.init();
    return this.value_;
  }

  constructor(private readonly init: () => T) {}
}

// Inspired by AbortController, which we may wish to switch to if it becomes standardized enough
// across node and browser js.
export class CancelController {
  private readonly listeners_: Array<() => void> = [];

  signal(cb: () => void): void {
    this.listeners_.push(cb);
  }

  // Calls every signal() registered since the last call to cancel().
  cancel() {
    const listeners = [...this.listeners_];
    this.listeners_.length = 0;
    for (const l of listeners) {
      try {
        l();
      } finally {
        // This space intentionally left blank.
      }
    }
  }

  reset() {
    this.listeners_.length = 0;
  }
}

export class AsyncCancelError extends CustomError {}

export function wait(delay?: Scalar<Time>, controller?: CancelController) {
  return new Promise<void>((resolve, reject) => {
    const to = setTimeout(resolve, delay?.value(Milliseconds) ?? 0);
    if (controller) {
      controller.signal(() => {
        clearTimeout(to);
        reject(new AsyncCancelError());
      });
    }
  });
}

export class AsyncTimeoutError extends CustomError {}

export function withTimeout<T>(promise: Promise<T>, timeout: Scalar<Time>) {
  return new Promise<T>((resolve, reject) => {
    const to = setTimeout(() => {
      reject(new AsyncTimeoutError());
    }, timeout.value(Milliseconds));
    promise
      .then((result) => {
        clearTimeout(to);
        resolve(result);
      })
      .catch((err) => {
        // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
        reject(err);
      });
  });
}

// Functions that are optionally asynchronous can take this to trigger the async version.
export const Async = Symbol('Async');

const debug = DEV;

export function ignore(promise: Promise<unknown> | undefined) {
  if (debug) {
    promise?.catch((reason) => {
      console.error('Promise error', reason);
    });
  }
}

export interface ManualPromise<T = void> {
  promise: Promise<T>;
  resolve: (value: T | PromiseLike<T>) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  reject: (reason?: any) => void;
}

export function manualPromise<T = void>(): ManualPromise<T> {
  let exportedResolve!: (value: T | PromiseLike<T>) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let exportedReject!: (reason?: any) => void;
  const promise = new Promise<T>((resolve, reject) => {
    exportedResolve = resolve;
    exportedReject = reject;
  });
  return {
    promise,
    resolve: exportedResolve,
    reject: exportedReject,
  };
}

export type DeferredPromise<T> = {
  start: () => void;
  promise: Promise<T>;
};

export function defer<T = void>(op: () => Promise<T>): DeferredPromise<T> {
  const mp = manualPromise();
  return { start: () => mp.resolve(), promise: mp.promise.then(op) };
}

export interface DebounceOptions {
  delay?: Scalar<Time>;
  onWait?: () => void;
}

// TODO: rxjs
export function debounce<T = void>(callback: (arg: T) => void, options?: DebounceOptions) {
  // A delay of zero will debounce until the stack unwinds.
  const delay = options?.delay ?? Milliseconds(0);
  let id: ReturnType<typeof setTimeout> | undefined;
  return (arg: T) => {
    options?.onWait?.();
    if (isPresent(id)) {
      clearTimeout(id);
    }
    id = setTimeout(() => {
      id = undefined;
      callback(arg);
    }, delay.value(Milliseconds));
  };
}

// A callback factory that delays and debounces all factory calls until the stack is unwound.
// If `disposed`, the callback is prevented from executing.
export class DebouncedCallback extends Tracked {
  readonly scheduler: () => void;

  constructor(cb: () => void) {
    super();
    const trigger = () => {
      this.#pending = undefined;
      cb();
    };
    this.scheduler = () => {
      if (this.#pending === undefined) {
        this.#pending = setTimeout(trigger, 0);
      }
    };
  }

  #pending?: ReturnType<typeof setTimeout> | undefined;

  override dispose() {
    if (this.#pending !== undefined) {
      clearTimeout(this.#pending);
      this.#pending = undefined;
    }
    return super.dispose();
  }
}
